/**
 * Shared table styling constants for Market Summary tables.
 * Used by both PDFReportRenderer (preview/export) and MarketTable (Live View)
 * to ensure consistent table appearance.
 *
 * PDF uses scoped classes under .ij-report-root (defined in pdf-report-legacy.css)
 * Live View uses global utility classes (defined in index.css)
 */

/**
 * Table structure class names for PDF context.
 * These work within the .ij-report-root scoped CSS.
 */
export const pdfTableStyles = {
  table: 'pdf-table ij-smart-widths',
  headerCell: 'pdf-table-header-cell whitespace-pre-line',
  bodyCell: 'pdf-table-cell',
  tableFrame: 'ij-table-frame',
  sectionHeader: 'pdf-section-header',
} as const

/**
 * Table structure class names for Live View context.
 * These use Tailwind utilities and global CSS classes.
 */
export const liveViewTableStyles = {
  // Main table wrapper with scroll
  tableWrapper: 'relative h-[90vh] overflow-auto border border-gray-200 custom-scrollbar',
  // Table element
  table: 'w-full divide-y divide-gray-200 ij-lv-table',
  // Header row
  thead: 'sticky top-0 z-10',
  // Header cell - includes border styling
  headerCell: 'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap ij-lv-header-cell',
  // Body cell - base styling
  bodyCell: 'px-4 py-3 text-sm text-gray-900 ij-lv-cell',
  // Body cell with vertical alignment matching PDF
  bodyCellCenter: 'px-4 py-3 text-sm text-gray-900 ij-lv-cell ij-lv-cell-center',
  bodyCellLeftCenter: 'px-4 py-3 text-sm text-gray-900 ij-lv-cell ij-lv-cell-leftcenter',
  bodyCellTopLeft: 'px-4 py-3 text-sm text-gray-900 ij-lv-cell ij-lv-cell-topleft',
  // Table body
  tbody: 'bg-white divide-y divide-gray-200',
} as const

/**
 * Get the appropriate cell style class based on alignment and context.
 * @param alignment - Cell alignment type
 * @param isPdfContext - True for PDF rendering, false for Live View
 */
export function getCellStyleClass(
  alignment: 'center' | 'leftcenter' | 'topleft',
  isPdfContext: boolean
): string {
  if (isPdfContext) {
    // PDF uses scoped alignment classes
    switch (alignment) {
      case 'center':
        return 'pdf-table-cell pdf-cell-center'
      case 'leftcenter':
        return 'pdf-table-cell pdf-cell-leftcenter'
      case 'topleft':
        return 'pdf-table-cell pdf-cell-topleft'
      default:
        return 'pdf-table-cell pdf-cell-center'
    }
  } else {
    // Live View uses global utility classes
    switch (alignment) {
      case 'center':
        return liveViewTableStyles.bodyCellCenter
      case 'leftcenter':
        return liveViewTableStyles.bodyCellLeftCenter
      case 'topleft':
        return liveViewTableStyles.bodyCellTopLeft
      default:
        return liveViewTableStyles.bodyCellCenter
    }
  }
}

/**
 * Border style constants matching the PDF look.
 * Header: 1px black border
 * Body cells: 1px light gray border (#E0E0E0)
 */
export const borderColors = {
  header: '#000000',
  body: '#E0E0E0',
  headerBg: '#E9E9E9',
} as const

